import React, {useCallback, useEffect, useRef, useState} from 'react';
import {LoadedImage} from '../EmblaCarousel/FactionsCarousel';
import '../YandexAuthPage/YandexAuthPage.css';
// Кнопка повтора — та же, что в оверлее разрыва соединения
import '../ConnectionLostOverlay/ConnectionLostOverlay.css';
import './NetworkErrorPage.css';
import VersionLabel from '../VersionLabel/VersionLabel';
import MusicMuteToggleButton from '../MusicMuteToggleButton/MusicMuteToggleButton';
import {sendAppEvent} from '../../analytics/gameAnalytics';
import {CONNECTIVITY_STATUS, diagnoseConnectivity} from '../../utils/network/startupConnectivity';

// Пока на экране ошибка, связь перепроверяется сама: игрок выключил VPN или
// сменил сеть — и игра открылась без лишних нажатий
export const NETWORK_RECHECK_INTERVAL_MS = 10000;

const CHECKING_TEXTS = {
  title: 'Нет связи с сервером',
  text: 'Проверяем подключение...',
  tips: [],
};

export const NETWORK_ERROR_TEXTS = {
  [CONNECTIVITY_STATUS.OFFLINE]: {
    title: 'Нет подключения к интернету',
    text: 'Устройство сейчас не может выйти в интернет.',
    tips: [
      'Проверьте Wi-Fi или мобильный интернет, попробуйте подключиться к другой сети.',
      'Если включен VPN или прокси, он может не пропускать трафик: выключите его или выберите другой сервер.',
    ],
  },
  [CONNECTIVITY_STATUS.SERVER_UNREACHABLE]: {
    title: 'Сервер игры недоступен',
    text: 'Интернет работает, но до сервера Стратегикона из этой сети достучаться не получается.',
    tips: [
      'Если включен VPN или прокси — выключите его или смените локацию сервера.',
      'Если VPN выключен — попробуйте включить его.',
      'Переключитесь с Wi-Fi на мобильный интернет или наоборот.',
    ],
  },
  [CONNECTIVITY_STATUS.SERVER_ERROR]: {
    title: 'Сервер временно не отвечает',
    text: 'Связь с сервером есть, но он сейчас не может ответить — возможно, идет обновление. Обычно это занимает несколько минут.',
    tips: [],
  },
};

/**
 * Экран «нет связи» на старте приложения: показывается вместо страницы входа,
 * когда проверка авторизации не дошла до сервера (обрыв сети, сервер
 * недоступен или не ответил за отведенное время). Раньше в этом случае
 * игрок, у которого сессия есть, видел предложение войти через Яндекс ID —
 * как будто его разлогинило.
 *
 * Сам выясняет, в чем дело (см. diagnoseConnectivity), и подсказывает, что
 * делать: нет интернета вообще, интернет есть, но наш сервер из этой сети
 * недоступен (VPN, прокси, ограничения сети), или сервер отвечает ошибкой.
 * Как только сервер отвечает — зовет onServerAvailable, и App повторяет
 * проверку авторизации. Перепроверка — по кнопке, по событию online и по
 * таймеру.
 *
 * Компоновка — та же, что у страницы входа (бюст, заголовок, копирайт,
 * версия), только вместо кнопок входа объяснение и кнопка повтора в стиле
 * оверлея разрыва соединения (см. ConnectionLostOverlay)
 */
function NetworkErrorPage({onServerAvailable, diagnose = diagnoseConnectivity}) {
  const [status, setStatus] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const isCheckingRef = useRef(false);
  const isUnmountedRef = useRef(false);
  const hasReportedRef = useRef(false);
  const onServerAvailableRef = useRef(onServerAvailable);
  onServerAvailableRef.current = onServerAvailable;

  const runCheck = useCallback(async () => {
    if (isCheckingRef.current) {
      return;
    }
    isCheckingRef.current = true;
    setIsChecking(true);
    let nextStatus;
    try {
      nextStatus = await diagnose();
    } catch (error) {
      console.error('Connectivity diagnosis failed', error);
      nextStatus = CONNECTIVITY_STATUS.SERVER_UNREACHABLE;
    }
    isCheckingRef.current = false;
    if (isUnmountedRef.current) {
      return;
    }
    // В статистику — только первый диагноз экрана: периодические
    // перепроверки утопили бы его в повторах
    if (!hasReportedRef.current) {
      hasReportedRef.current = true;
      sendAppEvent('network_diagnosis', nextStatus);
    }
    setIsChecking(false);
    if (nextStatus === CONNECTIVITY_STATUS.SERVER_OK) {
      onServerAvailableRef.current?.();
      return;
    }
    setStatus(nextStatus);
  }, [diagnose]);

  useEffect(() => {
    isUnmountedRef.current = false;
    runCheck();
    const intervalId = setInterval(runCheck, NETWORK_RECHECK_INTERVAL_MS);
    window.addEventListener('online', runCheck);
    return () => {
      isUnmountedRef.current = true;
      clearInterval(intervalId);
      window.removeEventListener('online', runCheck);
    };
  }, [runCheck]);

  const {title, text, tips} = NETWORK_ERROR_TEXTS[status] || CHECKING_TEXTS;

  return (
    <>
      <MusicMuteToggleButton pageId={'NetworkErrorPage'}/>
      <LoadedImage
        className={'loading-screen-bust main-menu-bust margin-top-auto'}
        src={'/img/other/strategikon_bust.png'}
      />
      <h1 className={'auth-page-title'}>Стратегикон</h1>
      <div className={'network-error-page-content'} data-status={status || 'checking'}>
        <h2 className={'network-error-page-title'}>{title}</h2>
        <p className={'network-error-page-text'}>{text}</p>
        {tips.length > 0 && (
          <ul className={'network-error-page-tips'}>
            {tips.map((tip) => (
              <li key={tip} className={'network-error-page-tip'}>{tip}</li>
            ))}
          </ul>
        )}
        {status && (
          <p className={'network-error-page-text network-error-page-text_hint'}>
            Игра подключится сама, как только связь появится.
          </p>
        )}
        <button className={'connection-lost-button'} onClick={runCheck} disabled={isChecking}>
          {isChecking ? 'Проверяем...' : 'Проверить снова'}
        </button>
      </div>
      <div className={'auth-page-copyright'}>
        {`™ Стратегикон, ${new Date().getFullYear()}, все права защищены`}
      </div>
      <VersionLabel/>
    </>
  );
}

export default NetworkErrorPage;
